import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { COMMON_IMPORTS } from '../../common-imports';
import { PanelModule } from 'primeng/panel';
import { GraphDataEthnicity, PercentileQualifier } from '../../backend-response-models/calc-enums.helper';
import { TranslatePipe } from '../../languages/translate.pipe';
import Mustache from 'mustache';
import { CalcTreatmentResponse, SerCalcResponse } from '../../backend-response-models/calc-interfaces.helper';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { ChartModule } from 'primeng/chart';
import { blackColorTL, greenColorTL, greyColorTL, orangeColorTL, redColorTL, whiteColorTL, yellowColorTL } from '../../helpers/ui.helper';
import { SerGraphComponent } from '../ser-graph/ser-graph.component';
import { Dataset } from '../../shared/shared-enums/enum-helper';
import { getNumberSuffix } from '../../shared/misc';

@Component({
  selector: 'app-ser-percentile',
  standalone: true,
  imports: [...COMMON_IMPORTS, PanelModule, DropdownModule, ChartModule, SerGraphComponent],
  templateUrl: './ser-percentile.component.html',
  styleUrl: './ser-percentile.component.css',
})
export class SerPercentileComponent implements OnInit, OnChanges {
  @Input({ required: true })
  serCalcResponse!: SerCalcResponse;

  @Input()
  personalization?: string;

  @Input()
  ethnicity?: GraphDataEthnicity;

  @Input({ required: true })
  treatmentCalcResponse!: CalcTreatmentResponse;

  @Output()
  ethnicityChanged = new EventEmitter<string>();

  public percentage?: number = 0;
  public serPercentileDescription: string = 'NONE';
  public percentagePercentile: string = 'NONE';

  public ethnicityDict: Record<GraphDataEthnicity, string> = {
    [GraphDataEthnicity.WESTERN]: 'WESTERN_DATA_CHOICE',
    [GraphDataEthnicity.EAST_ASIAN]: 'EAST_ASIAN_DATA_CHOICE',
  };
  public dataSets: Dataset[] = [];
  public selectedDataset?: Dataset;
  public ageOutOfBounds: boolean = false;

  constructor(private translatePipe: TranslatePipe) {}

  ngOnInit() {
    this.setupComponent();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(_changes: SimpleChanges) {
    this.setupComponent();
  }

  setupComponent() {
    this.addDataSets();

    if (!this.ethnicity) {
      this.ethnicity = GraphDataEthnicity.WESTERN;
    }

    this.selectedDataset = this.dataSets.find(d => d.value === this.ethnicity);
    this.updateAllStats();
  }

  private updateAllStats() {
    const ethicityData = this.serCalcResponse.ethnicities.find(e => e.graphDataEthnicity === this.ethnicity);
    this.percentage = ethicityData?.interpolatedPercentile ? Math.round(ethicityData?.interpolatedPercentile ?? 99) : undefined;

    let percentageStr = this.percentage?.toString() ?? '--';
    if (ethicityData?.decimalAge) {
      if (ethicityData?.decimalAge < 6 || ethicityData?.decimalAge > 16) {
        percentageStr = '--';
      }
    }

    const template = this.translatePipe.transform('SER_PERCENTILE_DESCRIPTION_TEMPLATE');
    const noNameName = this.translatePipe.transform('NO_NAME_NAME');
    const nameToUse = this.personalization ?? noNameName;
    const renderedTemplate = Mustache.render(template, { PERSONALIZED_NAME: nameToUse, PERCENT: percentageStr });

    this.serPercentileDescription = renderedTemplate;

    if (this.percentage === undefined) {
      this.percentagePercentile = '---';
      return;
    }

    const percentage = Math.floor(this.percentage);
    const suffix = getNumberSuffix(percentage);
    const percentile = this.translatePipe.transform('PERCENTILE');
    let prefix = '';

    if (ethicityData?.percentileQualifier === PercentileQualifier.ABOVE) {
      prefix = '>';
    }
    if (ethicityData?.percentileQualifier === PercentileQualifier.BELOW) {
      prefix = '<';
    }

    this.percentagePercentile = `${prefix} ${percentage}${suffix} ${percentile}`;

    if (ethicityData?.decimalAge) {
      if (ethicityData?.decimalAge < 6 || ethicityData?.decimalAge > 16) {
        this.ageOutOfBounds = true;
        this.percentagePercentile = this.translatePipe.transform('CAUTION_AGE_OUT_OF_BOUNDS');
      }
    }
  }

  public getPercentageColor(percentage: number) {
    if (percentage >= 90) {
      return redColorTL;
    }

    if (percentage >= 75) {
      return orangeColorTL;
    }

    if (percentage >= 50) {
      return orangeColorTL;
    }

    return greenColorTL;
  }

  public getTextColor(percentage: number) {
    if (percentage >= 90) {
      return whiteColorTL;
    }

    if (percentage >= 75) {
      return blackColorTL;
    }

    if (percentage >= 50) {
      return blackColorTL;
    }

    return whiteColorTL;
  }

  private addDataSets() {
    this.dataSets = this.serCalcResponse.ethnicities.map(e => {
      const keyForTranslation = this.ethnicityDict[e.graphDataEthnicity];
      return {
        label: keyForTranslation ? this.translatePipe.transform(keyForTranslation) : e.graphDataEthnicity.toString(),
        value: e.graphDataEthnicity,
      };
    });
  }

  async onDatasetChanged(event: DropdownChangeEvent) {
    this.ethnicity = event.value.value;
    this.selectedDataset = event.value;
    this.updateAllStats();
  }
}
